/**
 * Organization Transactions Store Slice
 *
 * Zustand slice for PRP-023 organization transactions page.
 * Handles advanced filtering, pagination, sorting, and URL state synchronization.
 * Related to Beads issue gw-spa-29v
 */

import { clearError as clearOpError, hasOperationLoaded, isOperationLoading } from '@gw/ui';

import { paymentsApi } from '@/api/payments';
import { createAsyncSliceAction } from '@/stores/helpers/asyncSliceAction';

import type {
  ErrorStates,
  ErrorStatuses,
  LoadedTimestamps,
  LoadingStates,
  TokenTransaction,
  TransactionFilters,
  TransactionPagination,
  TransactionSort,
} from '@gw/ui/types';
import type { StateCreator } from 'zustand';

// ============================================================================
// Slice Interface
// ============================================================================

export interface OrganizationTransactionsSlice {
  // State properties
  orgTransactions: TokenTransaction[];
  pagination: TransactionPagination;
  filters: TransactionFilters;
  sort: TransactionSort;
  isLoading: LoadingStates;
  hasLoaded: LoadingStates;
  lastLoaded: LoadedTimestamps;
  errors: ErrorStates;
  errorStatuses: ErrorStatuses;

  // Fetch transactions action
  fetchOrgTransactions: (orgId: string) => Promise<void>;

  // Filter management actions
  setFilters: (filters: Partial<TransactionFilters>) => void;
  clearFilters: () => void;

  // Sort management actions
  setSort: (sort: TransactionSort) => void;

  // Pagination management actions
  setPage: (page: number) => void;

  // URL state synchronization actions
  syncFromUrl: (params: URLSearchParams) => void;
  syncToUrl: () => URLSearchParams;

  // Utility actions
  clearError: (operation?: string) => void;
  checkIsLoading: (operation: string) => boolean;
  checkHasLoaded: (operation: string) => boolean;
}

// ============================================================================
// Slice Creator
// ============================================================================

export const createOrganizationTransactionsSlice: StateCreator<OrganizationTransactionsSlice> = (
  set,
  get
) => {
  // GW-8540: monotonic ticket incremented on every fetchOrgTransactions
  // call. fetchOrgTransactions has no request cancellation, so two calls
  // fired close together (e.g. clearing search immediately after the
  // debounced search request has already gone out) can resolve out of
  // order. Without a sequencing guard, whichever response lands LAST wins
  // — even if it was issued FIRST — so a slower, stale (e.g. filtered)
  // response can overwrite the newer, correct pagination.total/rows after
  // the filter was already cleared. Capturing the ticket at call time and
  // only committing a response when it's still the most recently issued
  // request drops stale, superseded responses instead.
  let fetchSequence = 0;

  return {
    // State initialization
    orgTransactions: [],
    pagination: {
      total: 0,
      limit: 50,
      offset: 0,
      hasMore: false,
    },
    filters: {},
    sort: {
      field: 'createdAt',
      order: 'desc',
    },
    isLoading: {},
    hasLoaded: {},
    lastLoaded: {},
    errors: {},
    errorStatuses: {},

    // Fetch organization transactions with current filters, sort, and pagination
    fetchOrgTransactions: async (orgId: string) => {
      const operation = 'fetchTransactions';
      const state = get();
      const ticket = ++fetchSequence;

      // Build query parameters from current state
      const params: {
        limit: number;
        offset: number;
        sortBy: TransactionSort['field'];
        sortOrder: TransactionSort['order'];
      } & {
        types?: TransactionFilters['types'];
        statuses?: TransactionFilters['statuses'];
        applicationIds?: string[];
        userIds?: string[];
        startDate?: string;
        endDate?: string;
        search?: string;
      } = {
        limit: state.pagination.limit,
        offset: state.pagination.offset,
        sortBy: state.sort.field,
        sortOrder: state.sort.order,
      };

      // Add filters to params
      if (state.filters.types?.length) {
        params.types = state.filters.types;
      }
      if (state.filters.statuses?.length) {
        params.statuses = state.filters.statuses;
      }
      if (state.filters.applicationIds?.length) {
        params.applicationIds = state.filters.applicationIds;
      }
      if (state.filters.userIds?.length) {
        params.userIds = state.filters.userIds;
      }
      if (state.filters.startDate) {
        params.startDate = state.filters.startDate;
      }
      if (state.filters.endDate) {
        params.endDate = state.filters.endDate;
      }
      if (state.filters.search) {
        params.search = state.filters.search;
      }

      await createAsyncSliceAction(
        operation,
        () => paymentsApi.getOrganizationTransactions(orgId, params),
        (response: { transactions: TokenTransaction[]; pagination: TransactionPagination }) => {
          // A newer fetchOrgTransactions call has been issued since this one
          // started — drop this stale response rather than clobber the
          // newer request's (possibly already-applied) state.
          if (ticket !== fetchSequence) {
            return {};
          }
          return {
            orgTransactions: response.transactions,
            pagination: response.pagination,
          };
        },
        set,
        get
      )();
    },

    // Set filters (merges with existing filters)
    setFilters: (newFilters: Partial<TransactionFilters>) => {
      const currentState = get();
      set({
        filters: {
          ...currentState.filters,
          ...newFilters,
        },
        // Reset pagination to first page when filters change
        pagination: {
          ...currentState.pagination,
          offset: 0,
        },
      });
    },

    // Clear all filters
    clearFilters: () => {
      const currentState = get();
      set({
        filters: {},
        // Reset pagination to first page
        pagination: {
          ...currentState.pagination,
          offset: 0,
        },
      });
    },

    // Set sort configuration
    setSort: (newSort: TransactionSort) => {
      const currentState = get();
      set({
        sort: newSort,
        // Reset pagination to first page when sort changes
        pagination: {
          ...currentState.pagination,
          offset: 0,
        },
      });
    },

    // Set page number (1-indexed)
    setPage: (page: number) => {
      const currentState = get();
      const offset = (page - 1) * currentState.pagination.limit;
      set({
        pagination: {
          ...currentState.pagination,
          offset,
        },
      });
    },

    // Sync state from URL search params
    syncFromUrl: (params: URLSearchParams) => {
      const filters: TransactionFilters = {};

      // Parse filter arrays
      if (params.has('types')) {
        filters.types = params.get('types')!.split(',') as TransactionFilters['types'];
      }
      if (params.has('statuses')) {
        filters.statuses = params.get('statuses')!.split(',') as TransactionFilters['statuses'];
      }
      if (params.has('applicationIds')) {
        filters.applicationIds = params.get('applicationIds')!.split(',');
      }
      if (params.has('userIds')) {
        filters.userIds = params.get('userIds')!.split(',');
      }

      // Parse date filters
      if (params.has('startDate')) {
        filters.startDate = params.get('startDate')!;
      }
      if (params.has('endDate')) {
        filters.endDate = params.get('endDate')!;
      }

      // Parse search
      if (params.has('search')) {
        filters.search = params.get('search')!;
      }

      // Parse sort
      const sort: TransactionSort = { field: 'createdAt', order: 'desc' };
      if (params.has('sortBy')) {
        sort.field = params.get('sortBy') as TransactionSort['field'];
      }
      if (params.has('sortOrder')) {
        sort.order = params.get('sortOrder') as 'asc' | 'desc';
      }

      // Parse page (1-indexed in URL)
      const currentState = get();
      let offset = 0;
      if (params.has('page')) {
        const page = parseInt(params.get('page')!, 10);
        offset = (page - 1) * currentState.pagination.limit;
      }

      set({
        filters,
        sort,
        pagination: {
          ...currentState.pagination,
          offset,
        },
      });
    },

    // Generate URL search params from current state
    syncToUrl: (): URLSearchParams => {
      const currentState = get();
      const params = new URLSearchParams();

      // Add filters to params
      if (currentState.filters.types?.length) {
        params.set('types', currentState.filters.types.join(','));
      }
      if (currentState.filters.statuses?.length) {
        params.set('statuses', currentState.filters.statuses.join(','));
      }
      if (currentState.filters.applicationIds?.length) {
        params.set('applicationIds', currentState.filters.applicationIds.join(','));
      }
      if (currentState.filters.userIds?.length) {
        params.set('userIds', currentState.filters.userIds.join(','));
      }
      if (currentState.filters.startDate) {
        params.set('startDate', currentState.filters.startDate);
      }
      if (currentState.filters.endDate) {
        params.set('endDate', currentState.filters.endDate);
      }
      if (currentState.filters.search) {
        params.set('search', currentState.filters.search);
      }

      // Add sort to params
      params.set('sortBy', currentState.sort.field);
      params.set('sortOrder', currentState.sort.order);

      // Add page to params (1-indexed in URL)
      const page = Math.floor(currentState.pagination.offset / currentState.pagination.limit) + 1;
      if (page > 1) {
        params.set('page', page.toString());
      }

      return params;
    },

    // Utility actions
    clearError: (operation?: string) => {
      const currentState = get();
      if (operation) {
        set({ errors: clearOpError(currentState.errors, operation) });
      } else {
        set({ errors: {} });
      }
    },
    checkIsLoading: (operation: string) => isOperationLoading(get().isLoading, operation),
    checkHasLoaded: (operation: string) => hasOperationLoaded(get().hasLoaded, operation),
  };
};
